import axios, { type AxiosInstance } from 'axios'
import { useAuthStore } from '../store/authStore'
import { refreshSession } from '../auth/session'

/**
 * 토큰 첨부 + 401 재발급 재시도. EDGE 토큰 하나로 여러 앱 API 를 부르므로(aud 에 앱 목록이 실린다)
 * 이 배관을 다른 앱의 인스턴스(api/board.ts)도 그대로 쓴다.
 */
export function attachAuth(instance: AxiosInstance): AxiosInstance {
  // Request: AccessToken 자동 첨부
  instance.interceptors.request.use((config) => {
    const token = useAuthStore.getState().accessToken
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  })

  // Response: 401이면 RefreshToken으로 재발급 후 재시도.
  // 갱신 자체는 auth/session.ts 의 단일 함수가 담당한다 (선제 갱신 타이머와 동시 실행 방지, 실패 시 로그아웃 처리 포함).
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config
      if (error.response?.status === 401 && !original._retry) {
        original._retry = true
        const newToken = await refreshSession()
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`
          return instance(original)
        }
      }
      return Promise.reject(error)
    }
  )
  return instance
}

const api = attachAuth(axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
}))

/** API 오류에서 사용자에게 보여줄 메시지. 서버(ExceptionMiddleware)가 준 `{ error }` 가 있으면 그것, 없으면 fallback. */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    const msg = data?.error ?? data?.message // message = 보드 API 오류 형식
    if (msg) return msg
    if (err.response?.status === 403) return '권한이 없습니다.'
  }
  return fallback
}

export default api
